import { KeysHolderNew } from './KeysHolder.js';
import { projectKeyHolderNew } from './KeysHolder.js';
import { articlesById } from '@/components/register/articlesById.js';
import api from './api.js';

export const wiringKeyHolder = new KeysHolderNew('Wirings');

/* ------------------------------------------------------------------ *
 *  New wiring data model (since the 2025 Excel import).
 *
 *  Backend `Wire` = { id, year, data: <AES string>, projectId: <Long|null> }
 *  decoded `data` = {
 *    budget_year, payment_date (epoch ms), sum,
 *    article_id, owner_id, org_id, project_id,
 *    is_fact (bool), is_cash (bool), comment
 *  }
 *
 *  Resolution strategy (architecture):
 *    1. fetch + decrypt all wires
 *    2. collect the distinct set of referenced project / org ids
 *    3. fetch ONLY those, by id list  (/supmain/projects/byIds, /legalentities/byIds)
 *    4. hydrate every wire against the resulting maps
 *
 *  After hydrate, wire.data = {
 *    ...raw fields kept as-is...,
 *    sum:Number, budgetYear, paymentDate(ISO date), comment, note,
 *    type:'fact'|'plan', paymentMethod:{id,text,value},
 *    article:<articlesById entry>|null, typeDir, fund:{fund}|null,
 *    expert:{id,fullName}|null, organization:{id,shortName,fullName}|null,
 *    project:{id,name,shortName}|null, projectMissing:<id>|false,
 *    contract:null, contractId:null, contractor:null, createDate:null,
 *    accountNumber:null, contribution:null
 *  }
 * ------------------------------------------------------------------ */

// --- experts (few, reused a lot -> one cached list) ---------------
let _expertsPromise = null;
const fullName = (e) =>
  [e.surname, e.name, e.middleName].filter(Boolean).join(' ').trim() || 'Без имени';

function getExpertsById() {
  if (!_expertsPromise) {
    _expertsPromise = api
      .get('supmain/experts?page=0&size=1000&sort=id,asc')
      .then((res) => {
        const map = {};
        for (const e of res.payload || []) map[e.id] = { id: e.id, fullName: fullName(e) };
        return map;
      })
      .catch(() => ({}));
  }
  return _expertsPromise;
}

// --- organisations: our orgs as one cached list + the rest by id list ---
let _ourOrgsPromise = null;
const _orgByIdCache = new Map(); // id -> {id, shortName, fullName} | null

function getOurOrgsById() {
  if (!_ourOrgsPromise) {
    _ourOrgsPromise = api
      .get('supmain/legalentities?page=0&size=1000&sort=id,asc&search=isOurOrg:true')
      .then((res) => {
        const map = {};
        for (const o of res.payload || []) {
          const v = { id: o.id, shortName: o.shortName, fullName: o.fullName };
          map[o.id] = v;
          _orgByIdCache.set(o.id, v);
        }
        return map;
      })
      .catch(() => ({}));
  }
  return _ourOrgsPromise;
}

async function getOrgsByIds(ids) {
  await getOurOrgsById();
  const missing = ids.filter((id) => id != null && !_orgByIdCache.has(id));
  if (missing.length) {
    const res = await api.get(`supmain/legalentities/byIds?ids=${missing.join(',')}`).catch(() => null);
    for (const o of (res && res.payload) || [])
      _orgByIdCache.set(o.id, { id: o.id, shortName: o.shortName, fullName: o.fullName });
    for (const id of missing) if (!_orgByIdCache.has(id)) _orgByIdCache.set(id, null);
  }
  const map = {};
  for (const id of ids) if (id != null) map[id] = _orgByIdCache.get(id) || null;
  return map;
}

// --- projects: only the referenced ids, by id list, decrypted once ---
const _projectByIdCache = new Map(); // id -> {id, name, shortName, year, numberInYear} | null

async function getProjectsByIds(ids) {
  const missing = ids.filter((id) => id != null && !_projectByIdCache.has(id));
  if (missing.length) {
    const res = await api.get(`supmain/projects/byIds?ids=${missing.join(',')}`).catch(() => null);
    await Promise.all(
      ((res && res.payload) || []).map(async (proj) => {
        let info = {};
        try {
          if (proj.data) info = (await projectKeyHolderNew.decode(proj.data)) || {};
        } catch (e) {
          info = {};
        }
        _projectByIdCache.set(proj.id, {
          id: proj.id,
          year: proj.year,
          numberInYear: proj.numberInYear,
          name: info.name || info.shortName || info.fullName || null,
          shortName: info.shortName || info.name || null,
        });
      })
    );
    for (const id of missing) if (!_projectByIdCache.has(id)) _projectByIdCache.set(id, null);
  }
  const map = {};
  for (const id of ids) if (id != null) map[id] = _projectByIdCache.get(id) || null;
  return map;
}

const paymentMethodOf = (isCash) =>
  isCash
    ? { id: 1, text: 'Наличный', value: 'cash' }
    : { id: 2, text: 'Безналичный', value: 'cashless' };

// step 1: decrypt wire.data in place (leaves it falsy if undecryptable)
async function decodeWire(wire) {
  const raw = await wiringKeyHolder.decode(wire.data);
  wire.data = raw && typeof raw === 'object' ? raw : undefined;
  return wire;
}

// step 4: build the render shape from the raw data + resolved maps
function hydrateWire(wire, { expertsById, orgsById, projectsById }) {
  const raw = wire.data;
  const projectId = wire.projectId ?? raw.project_id ?? null;
  const article = raw.article_id != null ? articlesById[raw.article_id] || null : null;
  const project = projectId != null ? projectsById[projectId] || null : null;

  wire.data = {
    ...raw,
    sum: Number(raw.sum) || 0,
    budgetYear: raw.budget_year ?? null,
    paymentDate: raw.payment_date ? new Date(raw.payment_date).toISOString().slice(0, 10) : null,
    comment: raw.comment || '',
    note: raw.comment || '',
    type: raw.is_fact ? 'fact' : 'plan',
    paymentMethod: paymentMethodOf(!!raw.is_cash),
    article,
    typeDir: article ? article.typeDir : null,
    fund: article ? { id: article.fund, fund: article.fund } : null,
    expert: raw.owner_id != null ? expertsById[raw.owner_id] || null : null,
    organization: raw.org_id != null ? orgsById[raw.org_id] || null : null,
    project,
    projectMissing: !project && projectId != null ? projectId : false,
    // fields the old model had but the new schema does not carry
    contract: null,
    contractId: null,
    contractor: null,
    createDate: null,
    accountNumber: null,
    contribution: null,
  };
  return wire;
}

// decrypt + hydrate a batch of wires (in place)
export const wDec = async (encArr) => {
  await wiringKeyHolder.checkKey();
  await Promise.all(encArr.map(decodeWire));
  const ok = encArr.filter((w) => w.data && typeof w.data === 'object');

  const projectIds = new Set();
  const orgIds = new Set();
  for (const w of ok) {
    const pid = w.projectId ?? w.data.project_id;
    if (pid != null) projectIds.add(pid);
    if (w.data.org_id != null) orgIds.add(w.data.org_id);
  }

  const [expertsById, orgsById, projectsById] = await Promise.all([
    getExpertsById(),
    getOrgsByIds([...orgIds]),
    getProjectsByIds([...projectIds]),
  ]);

  for (const w of ok) hydrateWire(w, { expertsById, orgsById, projectsById });
  return encArr;
};

// decrypt + hydrate a single wire
export const wDecOne = async (enc) => {
  await wDec([enc]);
  return enc;
};

// --- public API -------------------------------------------------
export const wGet = async (year = 2022) => {
  const res = await api.get(`supmain/wires/byYear/${year}`);
  return await wDec(res.payload || []);
};

export const wGetAll = async () => {
  const res = await api.get(`supmain/wires`);
  return await wDec(res.payload || []);
};

// wires of a single project (backend filters by the FK)
export const wGetByProject = async (projectId) => {
  if (projectId == null) return [];
  const res = await api.get(`supmain/wires/byProject/${projectId}`);
  return await wDec(res.payload || []);
};

export const wGetById = async (id) => {
  const res = await api.get(`/supmain/wires/${id}`);
  return res.payload;
};

export const wResetKey = async () => {
  wiringKeyHolder.resetKey();
};

// build the encrypted `data` blob (new flat schema) from a form model
const toRawData = (m) => ({
  budget_year: m.budgetYear ?? m.budget_year ?? null,
  payment_date: m.paymentDate
    ? typeof m.paymentDate === 'number'
      ? m.paymentDate
      : new Date(m.paymentDate).getTime()
    : null,
  sum: Number(m.sum) || 0,
  article_id: m.article?.id ?? m.articleId ?? m.article_id ?? null,
  owner_id: m.expert?.id ?? m.ownerId ?? m.owner_id ?? null,
  org_id: m.organization?.id ?? m.orgId ?? m.org_id ?? null,
  project_id: m.project?.id ?? m.projectId ?? m.project_id ?? null,
  is_fact: m.type ? m.type === 'fact' : !!m.is_fact,
  is_cash: m.paymentMethod ? m.paymentMethod.value === 'cash' || m.paymentMethod.id === 1 : !!m.is_cash,
  comment: m.comment ?? m.note ?? '',
});

export const wUpdate = async (id, year, model) => {
  await wiringKeyHolder.checkKey();
  const m = typeof model === 'string' ? JSON.parse(model) : model;
  const data = await wiringKeyHolder.encode(JSON.stringify(toRawData(m)));
  const projectId = m.project?.id ?? m.projectId ?? m.project_id ?? null;
  return api.putJson(`/supmain/wires`, { id, year, data, projectId: projectId || null });
};

export const wPost = async (year, model) => {
  await wiringKeyHolder.checkKey();
  const m = typeof model === 'string' ? JSON.parse(model) : model;
  const data = await wiringKeyHolder.encode(JSON.stringify(toRawData(m)));
  const projectId = m.project?.id ?? m.projectId ?? m.project_id ?? null;
  const res = await api.postJson('/supmain/wires', { year, data, projectId: projectId || null });
  return res.status;
};

export const wDelete = async (id) => {
  await wiringKeyHolder.checkKey();
  const res = await api.delete(`/supmain/wires/${id}`);
  return res.status;
};
